/**
 * What a proposal comes to, for whichever package and add-ons are chosen.
 *
 * A proposal is a quote with packages ("Standard", "Premium") and add-ons.
 * Three kinds of line:
 *
 * - **Included** — no package, not optional. In every package.
 * - **In a package** — `optionId` set. Counted only when that package is taken.
 * - **Add-on** — `isOptional`. Offered, drawn, and left out of the total until
 *   it is ticked. An add-on with an `optionId` is only offered with that
 *   package; one without is offered with any.
 *
 * The office's builder, the customer's page, the PDF and the server's own
 * acceptance all price through this one function. Four places doing their own
 * sum is four places that can disagree about a figure a customer signed, and
 * the one that would be wrong is whichever the customer was looking at.
 *
 * Integer minor units only, summed from already-rounded line totals — the same
 * arithmetic as `money.sumLines`, which this package cannot import because
 * `@fieldops/db` depends on it. A document discount comes off the subtotal and
 * not the tax, as it always has. Decision 80.
 */

export interface ProposalLineFigures {
  /** A `QuoteLine` id, or a client-side key before the line is saved. */
  id: string
  /** The package it belongs to — an option id, or a key. Null is "included". */
  optionId?: string | null
  isOptional?: boolean
  lineTotalExTax: number
  lineTax: number
  lineTotalIncTax: number
}

export interface ProposalOptionLike {
  id: string
  isRecommended?: boolean
  sortOrder?: number
}

export interface ProposalChoice {
  optionId?: string | null
  addOnIds?: readonly string[]
}

export interface ProposalFigures {
  subtotalExTax: number
  totalTax: number
  totalIncTax: number
}

export interface ProposalTotals {
  /** What the chosen package and ticked add-ons come to. */
  chosen: ProposalFigures & { optionId: string | null; lineIds: string[] }
  /** Each package on its own, with no add-ons — what a package card says. */
  packages: Array<ProposalFigures & { optionId: string }>
}

/**
 * The package a proposal describes before anybody chooses.
 *
 * The recommended one, else the first. What the quote's own `totalIncTax`
 * holds, so the Quotes list and the pipeline show a figure that is on the page.
 */
export function defaultOptionId(options: readonly ProposalOptionLike[]): string | null {
  if (options.length === 0) return null

  const ordered = [...options].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
  return (ordered.find((option) => option.isRecommended) ?? ordered[0])!.id
}

/** The add-ons that may be ticked alongside a package. */
export function availableAddOns<T extends ProposalLineFigures>(
  lines: readonly T[],
  optionId: string | null,
): T[] {
  return lines.filter(
    (line) => line.isOptional && (!line.optionId || line.optionId === optionId),
  )
}

/** The lines that are paid for under a choice: included, package, ticked. */
export function chosenLines<T extends ProposalLineFigures>(
  lines: readonly T[],
  optionId: string | null,
  addOnIds: readonly string[] = [],
): T[] {
  const ticked = new Set(addOnIds)
  const offered = new Set(availableAddOns(lines, optionId).map((line) => line.id))

  return lines.filter((line) => {
    if (line.isOptional) return ticked.has(line.id) && offered.has(line.id)
    return !line.optionId || line.optionId === optionId
  })
}

function figures(lines: readonly ProposalLineFigures[], discountAmount: number): ProposalFigures {
  const subtotalExTax = lines.reduce((sum, line) => sum + line.lineTotalExTax, 0) - discountAmount
  const totalTax = lines.reduce((sum, line) => sum + line.lineTax, 0)
  return { subtotalExTax, totalTax, totalIncTax: subtotalExTax + totalTax }
}

/**
 * The totals for a choice, and for every package on its own.
 *
 * With no choice, the default package and no add-ons — which on a quote with
 * no packages is simply every line that is not optional.
 */
export function proposalTotals(
  document: {
    lines: readonly ProposalLineFigures[]
    options?: readonly ProposalOptionLike[]
    discountAmount?: number
  },
  choice: ProposalChoice = {},
): ProposalTotals {
  const options = document.options ?? []
  const discount = document.discountAmount ?? 0
  const optionId =
    choice.optionId !== undefined && choice.optionId !== null
      ? choice.optionId
      : defaultOptionId(options)

  const chosen = chosenLines(document.lines, optionId, choice.addOnIds ?? [])

  return {
    chosen: {
      ...figures(chosen, discount),
      optionId,
      lineIds: chosen.map((line) => line.id),
    },
    packages: [...options]
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .map((option) => ({
        optionId: option.id,
        ...figures(chosenLines(document.lines, option.id), discount),
      })),
  }
}

/**
 * Why a choice cannot be accepted, or null when it can.
 *
 * The server asks this before it writes an acceptance, and the customer's page
 * asks it before it enables the button, so the sentence the customer reads is
 * the one the server would have refused with.
 */
export function proposalChoiceProblem(
  document: {
    lines: readonly ProposalLineFigures[]
    options?: readonly ProposalOptionLike[]
  },
  choice: ProposalChoice,
): string | null {
  const options = document.options ?? []
  const optionId = choice.optionId ?? null

  if (options.length > 0 && !optionId) return 'Choose one of the options.'
  if (options.length === 0 && optionId) return 'This proposal has no options to choose from.'
  if (optionId && !options.some((option) => option.id === optionId)) {
    return 'That option is not on this proposal.'
  }

  const offered = new Set(availableAddOns(document.lines, optionId).map((line) => line.id))
  const addOnIds = choice.addOnIds ?? []

  if (new Set(addOnIds).size !== addOnIds.length) return 'An extra was chosen twice.'
  if (addOnIds.some((id) => !offered.has(id))) {
    return 'One of the extras chosen is not offered with that option.'
  }

  return null
}

/**
 * The photos and PDFs on a proposal block, in order.
 *
 * Only an `IMAGE` block carries files; a cover's `attachmentId` is its one
 * photograph. An `IMAGE` block saved before blocks held several kept its photo
 * in `attachmentId`, so that id is read as the first file — which is why every
 * reader goes through here rather than reading `fileIds`. Decision 82.
 */
export function sectionFileIds(section: {
  kind: string
  attachmentId?: string | null
  fileIds?: readonly string[] | null
}): string[] {
  if (section.kind !== 'IMAGE') return []
  return [...new Set([section.attachmentId, ...(section.fileIds ?? [])].filter((id): id is string => !!id))]
}

/** Whether a proposal file draws as a photograph rather than a download. */
export function isProposalImage(mimeType: string): boolean {
  return /^image\/(jpeg|png|gif|webp|avif)$/.test(mimeType)
}

/** A unit price with its tax, as the customer's table prints "Price inc GST". */
export function unitPriceIncTax(line: {
  unitPrice: number
  unitPriceIncTax?: number | null
  taxRateBasisPoints: number
}): number {
  if (line.unitPriceIncTax != null) return line.unitPriceIncTax
  return Math.round((line.unitPrice * (10_000 + line.taxRateBasisPoints)) / 10_000)
}

/**
 * Why a customer turned a proposal down.
 *
 * A short fixed list plus an optional note, because the point of asking is to
 * count the answers, and free text does not count. **Provisional**: the list is
 * a business judgement and is Gavin's to confirm — the keys are stored, so
 * relabelling one later is free and renaming a key is a data migration.
 */
export const DECLINE_REASONS = [
  { key: 'PRICE', label: 'The price' },
  { key: 'TIMING', label: 'The timing does not suit' },
  { key: 'WENT_ELSEWHERE', label: 'Going with someone else' },
  { key: 'NO_LONGER_NEEDED', label: 'No longer needed' },
  { key: 'OTHER', label: 'Something else' },
] as const

export type DeclineReasonKey = (typeof DECLINE_REASONS)[number]['key']

export const DECLINE_REASON_KEYS = DECLINE_REASONS.map((reason) => reason.key) as [
  DeclineReasonKey,
  ...DeclineReasonKey[],
]

/**
 * The reasons the office can close a quote with. The customer's own list plus
 * one only the office can give — nobody answered — which is how most quotes
 * that are never won actually end. Not offered on the customer's page, where
 * it would make no sense. Stored in the same `declineCategory`.
 */
export const OFFICE_DECLINE_REASONS = [
  ...DECLINE_REASONS.filter((reason) => reason.key !== 'OTHER'),
  { key: 'NO_REPLY', label: 'Never heard back' },
  { key: 'OTHER', label: 'Something else' },
] as const

export type OfficeDeclineReasonKey = (typeof OFFICE_DECLINE_REASONS)[number]['key']

export const OFFICE_DECLINE_REASON_KEYS = OFFICE_DECLINE_REASONS.map((reason) => reason.key) as [
  OfficeDeclineReasonKey,
  ...OfficeDeclineReasonKey[],
]

export function declineReasonLabel(key: string | null | undefined): string | null {
  if (!key) return null
  return OFFICE_DECLINE_REASONS.find((reason) => reason.key === key)?.label ?? key
}

/**
 * A proposal body as paragraphs and bullet lists.
 *
 * The office types plain text: a blank line between paragraphs, `- ` at the
 * start of a line for a bullet. Parsed here once so the builder's preview, the
 * customer's page and the PDF break the same text the same way. There is no
 * HTML anywhere in this, on purpose — the text reaches a public page and a
 * PDF, and markup typed into a textarea has no business reaching either.
 */
export type ProposalBlock =
  | { kind: 'paragraph'; text: string }
  | { kind: 'list'; items: string[] }

export function proposalBlocks(body: string | null | undefined): ProposalBlock[] {
  if (!body) return []

  const blocks: ProposalBlock[] = []

  for (const chunk of body.replace(/\r\n?/g, '\n').split(/\n\s*\n/)) {
    const lines = chunk.split('\n').map((line) => line.trimEnd()).filter((line) => line.trim() !== '')
    if (lines.length === 0) continue

    let paragraph: string[] = []
    let list: string[] = []

    const flushParagraph = () => {
      if (paragraph.length > 0) blocks.push({ kind: 'paragraph', text: paragraph.join('\n') })
      paragraph = []
    }
    const flushList = () => {
      if (list.length > 0) blocks.push({ kind: 'list', items: list })
      list = []
    }

    for (const line of lines) {
      const bullet = /^\s*[-*•]\s+(.*)$/.exec(line)
      if (bullet) {
        flushParagraph()
        list.push(bullet[1]!.trim())
      } else {
        flushList()
        paragraph.push(line.trim())
      }
    }

    flushParagraph()
    flushList()
  }

  return blocks
}
